import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Collapse,
    CircularProgress,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    InputAdornment,
    useTheme,
    Switch,
    Button,
    DialogActions,
    Chip,
    Tooltip,
    TablePagination
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, QrCode2, Search, Edit, Block, CheckCircle, Warning, OpenInNew } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import { getFrontendUrl, generatePublicUrl } from '../../utils/urlUtils';

const QRDialog = ({ open, onClose, value, title }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <QRCodeSVG 
                value={value} 
                size={256} 
                level="H"
                includeMargin={true}
                fgColor="#000000"
                bgColor="#ffffff"
            />
        </DialogContent>
    </Dialog>
);

const ConfirmationDialog = ({ open, onClose, onConfirm, title, message, confirmText = "Confirm", severity = "warning" }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {severity === 'warning' && <Warning color="warning" />}
            {severity === 'error' && <Block color="error" />}
            {title}
        </DialogTitle>
        <DialogContent>
            <Typography>{message}</Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} color="primary">Cancel</Button>
            <Button onClick={onConfirm} color={severity} variant="contained">{confirmText}</Button>
        </DialogActions>
    </Dialog>
);

const UserStatusChip = ({ isActive }) => (
    <Chip
        icon={isActive ? <CheckCircle /> : <Block />}
        label={isActive ? 'Active' : 'Disabled'}
        color={isActive ? 'success' : 'error'}
        variant={isActive ? 'filled' : 'outlined'}
        size="small"
    />
);

// Add format date utility function
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const Row = ({ user, showActions, onToggleCardEnabled, onToggleUserActive }) => {
    const [open, setOpen] = useState(false);
    const [qrCodeValue, setQrCodeValue] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, data: null });
    const theme = useTheme();
    const navigate = useNavigate();

    const getCardUrl = (slug) => generatePublicUrl(slug);

    const handleUserToggle = () => {
        setConfirmDialog({
            open: true,
            type: 'user',
            data: user,
            title: user.is_active ? 'Disable User Account' : 'Enable User Account',
            message: user.is_active 
                ? `This will disable "${user.username}" and make all their portfolios inaccessible. The user will not be able to log in.`
                : `This will enable "${user.username}" and restore access to their account.`,
            severity: user.is_active ? 'error' : 'success'
        });
    };

    const handleCardToggle = (card) => {
        setConfirmDialog({
            open: true,
            type: 'card',
            data: card,
            title: card.is_enabled ? 'Disable Portfolio' : 'Enable Portfolio',
            message: card.is_enabled 
                ? `This will disable the portfolio "${card.name}" and make it inaccessible to visitors.`
                : `This will enable the portfolio "${card.name}" and make it accessible to visitors.`,
            severity: card.is_enabled ? 'warning' : 'success'
        });
    };

    const handleConfirm = () => {
        if (confirmDialog.type === 'user' && onToggleUserActive) {
            onToggleUserActive(confirmDialog.data.id);
        } else if (confirmDialog.type === 'card' && onToggleCardEnabled) {
            onToggleCardEnabled(confirmDialog.data.id);
        }
        setConfirmDialog({ open: false, type: null, data: null });
    };

    const getCardAccessibilityIcon = (card) => {
        if (!user.is_active) {
            return <Tooltip title="User disabled - portfolio inaccessible"><Warning color="error" /></Tooltip>;
        }
        if (!card.is_enabled) {
            return <Tooltip title="Portfolio disabled"><Block color="warning" /></Tooltip>;
        }
        return <Tooltip title="Portfolio accessible"><CheckCircle color="success" /></Tooltip>;
    };

    return (
        <>
            <TableRow sx={{
                '& > *': { borderBottom: 'unset' },
                '&:hover': {
                    backgroundColor: theme.palette.action.hover
                },
                backgroundColor: !user.is_active ? 'rgba(244, 67, 54, 0.05)' : 'inherit'
            }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" fontWeight="bold">{user.username}</Typography>
                        <UserStatusChip isActive={user.is_active} />
                    </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell align="center">{user.cards.length}</TableCell>
                <TableCell>
                    <Typography variant="body2" color="text.secondary">
                        {formatDate(user.created_at)}
                    </Typography>
                </TableCell>
                {showActions && (
                    <TableCell align="center">
                        <Tooltip title={user.is_active ? "Disable user and all portfolios" : "Enable user access"}>
                            <Switch
                                checked={user.is_active}
                                onChange={handleUserToggle}
                                color={user.is_active ? "success" : "default"}
                            />
                        </Tooltip>
                    </TableCell>
                )}
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={showActions ? 7 : 6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, padding: 2, backgroundColor: theme.palette.background.default, borderRadius: '8px' }}>
                            <Typography variant="h6" gutterBottom component="div" fontWeight="600">
                                User's Portfolios
                                {!user.is_active && (
                                    <Chip 
                                        label="All portfolios inaccessible due to disabled user" 
                                        color="error" 
                                        size="small" 
                                        sx={{ ml: 2 }}
                                    />
                                )}
                            </Typography>
                            <Table size="small" aria-label="user cards">
                                <TableHead>
                                    <TableRow sx={{ 
                                        backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black'
                                    }}>
                                        <TableCell sx={{ 
                                            color: 'white', 
                                            fontWeight: 'bold',
                                            borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                                        }}>Status</TableCell>
                                        <TableCell sx={{ 
                                            color: 'white', 
                                            fontWeight: 'bold',
                                            borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                                        }}>Portfolio Title</TableCell>
                                        <TableCell sx={{ 
                                            color: 'white', 
                                            fontWeight: 'bold',
                                            borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                                        }}>Link</TableCell>
                                        <TableCell align="right" sx={{ 
                                            color: 'white', 
                                            fontWeight: 'bold',
                                            borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                                        }}>QR Code</TableCell>
                                        {showActions && <TableCell align="center" sx={{ 
                                            color: 'white', 
                                            fontWeight: 'bold',
                                            borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                                        }}>Portfolio Toggle</TableCell>}
                                        {showActions && <TableCell align="center" sx={{ 
                                            color: 'white', 
                                            fontWeight: 'bold',
                                            borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                                        }}>Edit</TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {user.cards.map((card) => (
                                        <TableRow 
                                            key={card.id}
                                            sx={{
                                                backgroundColor: !user.is_active || !card.is_enabled ? 'rgba(255, 152, 0, 0.05)' : 'inherit'
                                            }}
                                        >
                                            <TableCell>
                                                {getCardAccessibilityIcon(card)}
                                            </TableCell>
                                            <TableCell>{card.name}</TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    href={getCardUrl(card.slug)}
                                                    target="_blank"
                                                    rel="noopener"
                                                    startIcon={<OpenInNew />}
                                                    disabled={!user.is_active || !card.is_enabled}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        textTransform: 'none',
                                                        fontSize: '0.7rem',
                                                        minWidth: 'auto',
                                                        px: 2,
                                                        py: 0.5,
                                                        ...((!user.is_active || !card.is_enabled) && {
                                                            textDecoration: 'line-through',
                                                            borderColor: 'text.disabled',
                                                            color: 'text.disabled'
                                                        })
                                                    }}
                                                >
                                                    View Card
                                                </Button>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title={(!user.is_active || !card.is_enabled) ? "Portfolio not accessible" : "Generate QR Code"}>
                                                    <span>
                                                        <IconButton 
                                                            onClick={() => setQrCodeValue(getCardUrl(card.slug))}
                                                            disabled={!user.is_active || !card.is_enabled}
                                                        >
                                                            <QrCode2 />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            {showActions && (
                                                <TableCell align="center">
                                                    <Tooltip title={!user.is_active ? "Cannot toggle - user is disabled" : (card.is_enabled ? "Disable portfolio" : "Enable portfolio")}>
                                                        <span>
                                                            <Switch
                                                                checked={card.is_enabled}
                                                                onChange={() => handleCardToggle(card)}
                                                                disabled={!user.is_active}
                                                                color={card.is_enabled ? "success" : "default"}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            )}
                                            {showActions && (
                                                <TableCell align="center">
                                                    <IconButton color="primary" onClick={() => navigate(`/edit-card/${card.slug}`)}>
                                                        <Edit />
                                                    </IconButton>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
            {qrCodeValue && (
                <QRDialog
                    open={!!qrCodeValue}
                    onClose={() => setQrCodeValue(null)}
                    value={qrCodeValue}
                    title="Scan QR Code"
                />
            )}
            <ConfirmationDialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false, type: null, data: null })}
                onConfirm={handleConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                severity={confirmDialog.severity}
                confirmText={confirmDialog.type === 'user' ? (confirmDialog.data?.is_active ? 'Disable User' : 'Enable User') : (confirmDialog.data?.is_enabled ? 'Disable Portfolio' : 'Enable Portfolio')}
            />
        </>
    );
};

const UsersTable = ({ users, loading, maxHeight = 600, showActions = false, onToggleCardEnabled, onToggleUserActive }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const theme = useTheme();

    const filteredUsers = useMemo(() => {
        return users.filter(user =>
            user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [users, searchTerm]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Get paginated users
    const paginatedUsers = filteredUsers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const activeUsers = users.filter(user => user.is_active).length;
    const disabledUsers = users.length - activeUsers;

    // Reset page when search changes
    useEffect(() => {
        setPage(0);
    }, [searchTerm]);

    return (
        <Paper elevation={3} sx={{
            p: { xs: 2, md: 3 },
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        User Management
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip label={`${activeUsers} Active`} color="success" size="small" />
                        <Chip label={`${disabledUsers} Disabled`} color="error" size="small" variant="outlined" />
                    </Box>
                </Box>
                <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                        sx: { borderRadius: '8px' }
                    }}
                />
            </Box>
            <TableContainer sx={{ 
                maxHeight: maxHeight, 
                flexGrow: 1,
                '&::-webkit-scrollbar': { width: '8px' }, 
                '&::-webkit-scrollbar-thumb': { 
                    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)', 
                    borderRadius: '4px' 
                } 
            }}>
                <Table stickyHeader aria-label="collapsible table">
                    <TableHead>
                        <TableRow sx={{ 
                            backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black'
                        }}>
                            <TableCell sx={{ 
                                backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black',
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                            }} />
                            <TableCell sx={{ 
                                color: 'white', 
                                fontWeight: 'bold',
                                backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black',
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                            }}>Username</TableCell>
                            <TableCell sx={{ 
                                color: 'white', 
                                fontWeight: 'bold',
                                backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black',
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                            }}>Email</TableCell>
                            <TableCell align="center" sx={{ 
                                color: 'white', 
                                fontWeight: 'bold',
                                backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black',
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                            }}>No. of Cards</TableCell>
                            <TableCell sx={{ 
                                color: 'white', 
                                fontWeight: 'bold',
                                backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black',
                                borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                            }}>Created</TableCell>
                            {showActions && (
                                <TableCell align="center" sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold',
                                    backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'black',
                                    borderBottom: theme.palette.mode === 'dark' ? '1px solid grey.600' : '1px solid black'
                                }}>User Access</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={showActions ? 6 : 5} align="center" sx={{ py: 5 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : paginatedUsers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={showActions ? 6 : 5} align="center" sx={{ py: 5 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No users found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedUsers.map((user) => (
                                <Row 
                                    key={user.id} 
                                    user={user} 
                                    showActions={showActions} 
                                    onToggleCardEnabled={onToggleCardEnabled}
                                    onToggleUserActive={onToggleUserActive}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredUsers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    mt: 'auto'
                }}
            />
        </Paper>
    );
};

export default UsersTable; 